import React, {createContext, useContext, useState, useEffect, PropsWithChildren} from 'react';
import {jwtDecode} from "jwt-decode";
import { Hub } from "@aws-amplify/core";
import { fetchAuthSession } from '@aws-amplify/auth';
import { Box, Spinner } from '@cloudscape-design/components';

export enum UserGroup {
    JUHSIS_ADMIN = "juhsis-admin",
    READ_ONLY = "read-only"
}

// Define the structure of the user data, including the API key
interface UserType {
    email?: string;
    name?: string;
    email_verified?: string;
    groups?: Set<UserGroup>;
    isAdmin?: boolean;
    isReadOnly?: boolean;
}

// Creating the context with an undefined initial value
const UserContext = createContext<UserType | undefined>(undefined);

// UserProvider component
export const UserProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [userData, setUserData] = useState<UserType>({});
    const [loading, setLoading] = useState(true); // Add a loading state

    const fetchUserData = async () => {
        try {
            const session = await fetchAuthSession();
            const jwt     = parseJwtToken(session.tokens!.idToken!.toString());
            const groups = new Set(!jwt ? [] : (jwt['cognito:groups'] || []).filter(
                (g:string): g is UserGroup => Object.values(UserGroup).includes(g as UserGroup)
            ));
            setUserData({
                email:          jwt?.email,
                name:           jwt?.name,
                email_verified: jwt?.email_verified,
                groups:         groups,
                isAdmin:        groups.has(UserGroup.JUHSIS_ADMIN),
                isReadOnly:     groups.has(UserGroup.READ_ONLY),
            });
        } catch {
            setUserData({});
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        // initial load
        fetchUserData();

        // subscribe to auth events
        const removeListener = Hub.listen('auth', ({ payload: { event } }) => {
            if (event === 'signedIn' || event === 'signedOut') {
                fetchUserData();
            }
        });

        return () => {
            removeListener();
        };
    }, []);

    if (loading) {
        return (
            <Box
                textAlign="center"
                padding={{ vertical: 'xxxl' }}
            >
                <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spinner size="large" />
                </div>
            </Box>
        );
    }

    return (
        <UserContext.Provider value={userData}>
            {children}
        </UserContext.Provider>
    );
};

// Custom hook to use the UserContext
export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

interface JwtToken {
    'cognito:username': string;
    email: string;
    'custom:company': string;
    name: string;
    email_verified: string;
    'custom:apikey': string;
    'cognito:groups': string[];
}

function parseJwtToken(token: string): JwtToken | undefined {
    try {
        return jwtDecode<JwtToken>(token!);
    } catch (error) {
        console.error("Failed to decode token or extract custom field:", error);
    }
}
